import React, { useEffect, useState } from 'react'
import { GpButton, Icon } from '../../components/gp'

const PRESETS = [25, 50, 75, 100]

const FundsSelect = (props) => {
    const [recommendedFunds, setRecommendedFunds] = useState(25)
    const [amount, setAmount] = useState(null)
    const [customAmount, setCustomAmount] = useState('')
    const [usingCustom, setUsingCustom] = useState(false)
    const [showAmountError, setShowAmountError] = useState(false)
    const [acknowledged, setAcknowledged] = useState(false)

    const evLicenseFee = (props.selectedProperty?.billingModel?.fees || []).find(
        (fee) => fee.name?.toLowerCase() === 'ev_license_fee'
    )

    const calculateRecommendedFunds = () => {
        let calculatedAmount
        const calculation = Math.round(
            (4 * Number(props.milesPerWeek) * 0.22) / 3.5
        )
        if (calculation < 50) calculatedAmount = 25
        else if (calculation < 75) calculatedAmount = 50
        else if (calculation < 100) calculatedAmount = 75
        else calculatedAmount = 100
        setAmount(calculatedAmount)
        setRecommendedFunds(calculatedAmount)
    }

    useEffect(() => {
        if (props.funds) {
            setAmount(props.funds)
            setRecommendedFunds(props.funds)
            if (!PRESETS.includes(props.funds)) {
                setUsingCustom(true)
                setCustomAmount(String(props.funds))
            }
        } else {
            calculateRecommendedFunds()
        }
    }, [])

    useEffect(() => {
        if (!usingCustom) {
            setShowAmountError(false)
            return
        }
        const n = Number(customAmount)
        if (!isNaN(n) && n >= 25 && n <= 100) {
            setAmount(n)
            setShowAmountError(false)
        } else {
            setShowAmountError(true)
        }
    }, [usingCustom, customAmount])

    const handlePreset = (p) => {
        setUsingCustom(false)
        setAmount(p)
        setCustomAmount('')
    }

    const handleContinue = () => {
        if (amount && !showAmountError) {
            props.fundsSelected(amount)
        }
    }

    const canSubmit =
        amount != null &&
        !showAmountError &&
        amount >= 25 &&
        amount <= 100 &&
        (!evLicenseFee || acknowledged)

    return (
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {/* Lime-soft info card */}
            <div
                style={{
                    display: 'flex',
                    gap: 14,
                    background: 'var(--gp-lime-soft)',
                    borderRadius: 'var(--gp-r-md)',
                    padding: '14px 16px',
                    marginBottom: 24,
                    alignItems: 'flex-start',
                }}
            >
                <div
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        background: 'var(--gp-card)',
                        color: 'var(--gp-brand)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Icon name="bolt" size={16} />
                </div>
                <div
                    style={{
                        font: 'var(--gp-body)',
                        color: 'var(--gp-ink)',
                        lineHeight: 1.5,
                    }}
                >
                    You drive about{' '}
                    <strong style={{ fontWeight: 500 }}>
                        {props.milesPerWeek} miles
                    </strong>{' '}
                    a week, so we recommend starting with{' '}
                    <strong style={{ fontWeight: 500 }}>${recommendedFunds}</strong> —
                    typically a month of charging, depending on your vehicle. We'll ask
                    for your card on the next step, but won't charge it until your property
                    manager approves you. We'll auto top-up your balance to{' '}
                    <strong style={{ fontWeight: 500 }}>${recommendedFunds}</strong>{' '}
                    whenever it falls below <strong style={{ fontWeight: 500 }}>$10</strong>.
                </div>
            </div>

            {/* Preset buttons */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 12,
                    marginBottom: 14,
                }}
            >
                {PRESETS.map((p) => {
                    const active = !usingCustom && amount === p
                    const isRec = p === recommendedFunds
                    return (
                        <button
                            key={p}
                            type="button"
                            onClick={() => handlePreset(p)}
                            style={{
                                height: 88,
                                padding: '0 16px',
                                borderRadius: 'var(--gp-r-md)',
                                border:
                                    '1.5px solid ' +
                                    (active ? 'var(--gp-brand)' : 'var(--gp-hairline)'),
                                background: active ? 'var(--gp-brand)' : 'var(--gp-card)',
                                color: active ? '#FFFFFF' : 'var(--gp-ink)',
                                cursor: 'pointer',
                                transition: 'all 0.12s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 4,
                            }}
                        >
                            <span
                                style={{
                                    font: '500 32px/1 var(--gp-font)',
                                    letterSpacing: -0.5,
                                }}
                            >
                                ${p}
                            </span>
                            {isRec && (
                                <span
                                    style={{
                                        font: '500 10px/1 var(--gp-font)',
                                        letterSpacing: 0.6,
                                        textTransform: 'uppercase',
                                        color: active ? 'var(--gp-lime)' : 'var(--gp-ink-mute)',
                                    }}
                                >
                                    Recommended
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>

            {/* Custom amount */}
            <div
                style={{
                    textAlign: 'center',
                    marginBottom: 18,
                }}
            >
                <div
                    style={{
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                        marginBottom: 6,
                    }}
                >
                    Or enter a custom amount
                </div>
                <div
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        background: 'var(--gp-card)',
                        border:
                            '1px solid ' +
                            (usingCustom
                                ? showAmountError
                                    ? 'var(--gp-danger)'
                                    : 'var(--gp-brand)'
                                : 'var(--gp-hairline)'),
                        borderRadius: 'var(--gp-r-md)',
                        minWidth: 220,
                    }}
                >
                    <span style={{ color: 'var(--gp-ink-mute)' }}>$</span>
                    <input
                        type="number"
                        value={customAmount}
                        onFocus={() => setUsingCustom(true)}
                        onChange={(e) => {
                            setUsingCustom(true)
                            setCustomAmount(e.target.value)
                        }}
                        placeholder="0.00"
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            font: '500 14px/1 var(--gp-mono)',
                            color: 'var(--gp-ink)',
                            width: '100%',
                            textAlign: 'left',
                        }}
                    />
                </div>
                {showAmountError && (
                    <div
                        style={{
                            font: 'var(--gp-caption)',
                            color: 'var(--gp-danger)',
                            marginTop: 6,
                        }}
                    >
                        Amount has to be between $25 and $100.
                    </div>
                )}
            </div>

            {evLicenseFee && (
                <label
                    style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        background: 'var(--gp-surface)',
                        border: '1px solid var(--gp-hairline)',
                        borderRadius: 'var(--gp-r-md)',
                        padding: '14px 16px',
                        marginBottom: 18,
                        cursor: 'pointer',
                    }}
                >
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, accentColor: 'var(--gp-brand)' }}
                    />
                    <span style={{ font: 'var(--gp-body)', color: 'var(--gp-ink)', lineHeight: 1.5 }}>
                        I understand this property charges an additional{' '}
                        <strong style={{ fontWeight: 500 }}>
                            ${(evLicenseFee.price / 100).toFixed(2)} monthly Access Fee
                        </strong>
                        .
                    </span>
                </label>
            )}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GpButton
                    variant="primary"
                    onClick={handleContinue}
                    disabled={!canSubmit}
                    style={{ minWidth: 200 }}
                >
                    Continue
                </GpButton>
            </div>
        </div>
    )
}

export default FundsSelect
